import React, { useState, useEffect, useRef } from 'react';
import {
  User, Lock, LockKeyhole, Eye, EyeOff, Fingerprint, ScanFace,
  Users, ClipboardCheck, Wallet, CreditCard, BarChart3,
  Package, NotebookPen, CalendarClock, FileCheck,
} from 'lucide-react';
import { supabase } from '../supabase';
import useStore from '../store/useStore';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { EdEaseMark, EdEaseWordmark, EdEaseLoader } from '../components/brand/EdEaseLogo';

const ACCENT     = 'oklch(0.52 0.16 285)';
const ACCENT_MED = 'oklch(0.45 0.17 285)';
const IDLE_BORDER = '#D3D5DE';

// Fingerprint / face sign-in (Android app only).
// Keys start with sb- so the sign-out cache wipes (which keep sb-*) leave them alone.
const IS_NATIVE = Capacitor.isNativePlatform();
const BIO_SERVER = 'edease';
const BIO_ON_KEY = 'sb-edease-bio-on';
const BIO_DECLINED_KEY = 'sb-edease-bio-declined';

// Mobile header look (values picked in the prototype)
const HERO_RADIUS = 28;   // px, bottom corners of the dark header
const HERO_SHADOW = 5;    // 0-10 strength of the drop shadow under it
const HERO_SHADOW_CSS = '0 ' + (HERO_SHADOW * 2.6) + 'px ' + (HERO_SHADOW * 6) + 'px -' + (HERO_SHADOW * 2) + 'px rgba(20, 10, 60, ' + (0.1 + HERO_SHADOW * 0.08).toFixed(2) + ')';

const FEATURES = [
  { label: 'Student Management', Icon: Users          },
  { label: 'Attendance',         Icon: ClipboardCheck },
  { label: 'Fee',                Icon: Wallet         },
  { label: 'POS',                Icon: CreditCard     },
  { label: 'Reports',            Icon: BarChart3      },
  { label: 'Asset Management',   Icon: Package        },
  { label: 'Diary',              Icon: NotebookPen    },
  { label: 'Time Table',         Icon: CalendarClock  },
  { label: 'Exam Manager',       Icon: FileCheck      },
];

// Deliberately scattered reveal order (not clockwise)
const REVEAL_ORDER = [4, 0, 7, 2, 8, 1, 5, 3, 6];

// Ray / box intersection — returns distance from center to the edge of a
// square tile of given half-extent, in the direction (ux, uy).
function boxHit(ux, uy, half) {
  const tx = Math.abs(ux) > 1e-6 ? half / Math.abs(ux) : Infinity;
  const ty = Math.abs(uy) > 1e-6 ? half / Math.abs(uy) : Infinity;
  return Math.min(tx, ty);
}

export default function LoginPage() {
  // Form state
  const [userId,    setUserId]    = useState('');
  const [password,  setPassword]  = useState('');
  const [pwVisible, setPwVisible] = useState(false);
  const [remember,  setRemember]  = useState(true);
  const [idFocused, setIdFocused] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [error,     setError]     = useState('');
  const loginNotice = useStore(s => s.loginNotice);   // set when a session was ended because the account could not be loaded
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [bioAvail, setBioAvail] = useState(false);
  const [bioOn, setBioOn] = useState(false);
  const [bioOffer, setBioOffer] = useState(false);

  useEffect(() => {
    if (!IS_NATIVE) return undefined;
    let cancelled = false;
    NativeBiometric.isAvailable()
      .then((r) => {
        if (cancelled || !r || !r.isAvailable) return;
        setBioAvail(true);
        try { if (localStorage.getItem(BIO_ON_KEY) === '1') setBioOn(true); } catch (e) { /* storage blocked */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [platform,  setPlatform]  = useState('android');

  // Two-stage vault animation: hub+lines at 800ms, module nodes at 1350ms
  const [vaultStage1, setVaultStage1] = useState(false);
  const [vaultStage2, setVaultStage2] = useState(false);
  const [vaultSize, setVaultSize] = useState({ w: 460, h: 400 });
  const vaultRef = useRef(null);

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) setPlatform('ios');
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setVaultStage1(true), 800);
    const t2 = setTimeout(() => setVaultStage2(true), 1350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    const el = vaultRef.current;
    if (!el || !window.ResizeObserver) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      if (w && h) setVaultSize({ w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Safety: if sign-in never completes, release the screen and say so
  useEffect(() => {
    if (!submitting) return undefined;
    const t = setTimeout(() => {
      setSubmitting(false);
      setError('Sign-in is taking too long. Please try again.');
    }, 20000);
    return () => clearTimeout(t);
  }, [submitting]);

  // Core sign-in. Returns { ok, invalid } so callers can react to a rejected password.
  const signInWith = async (rawId, pw, saveBio) => {
    setError('');
    useStore.getState().setLoginNotice('');
    setSubmitting(true);
    try {
      const raw = rawId.trim();
      const authEmail = raw.includes('@') ? raw : raw.toLowerCase() + '@login.internal';
      const { error: authErr } = await supabase.auth.signInWithPassword({ email: authEmail, password: pw });
      if (authErr) throw authErr;
      if (saveBio && IS_NATIVE) {
        try {
          await NativeBiometric.setCredentials({ username: raw, password: pw, server: BIO_SERVER });
          localStorage.setItem(BIO_ON_KEY, '1');
        } catch (e) { /* could not save, normal sign-in still proceeds */ }
      }
      // App.jsx onAuthStateChange → loadUser sets the user and unmounts this page.
      // Overlay stays visible until then; no setUser here to avoid racing loadUser.
      return { ok: true, invalid: false };
    } catch (err) {
      const msg = String(err && err.message ? err.message : '');
      setError(/banned/i.test(msg) ? 'This access has ended. Please contact EdEase for a new login.' : (msg || 'Invalid credentials'));
      setSubmitting(false);
      return { ok: false, invalid: String(err && err.message ? err.message : '').toLowerCase().includes('invalid') };
    }
  };

  const bioDeclined = () => {
    try { return localStorage.getItem(BIO_DECLINED_KEY) === '1'; } catch (e) { return true; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId.trim())  { setError('Please enter your user ID.');  return; }
    if (!password)       { setError('Please enter your password.'); return; }
    if (IS_NATIVE && bioAvail && !bioOn && remember && !bioDeclined()) { setBioOffer(true); return; }
    await signInWith(userId, password, false);
  };

  const bioPrompt = () => NativeBiometric.verifyIdentity({
    reason: 'Sign in to EdEase',
    title: 'Sign in to EdEase',
    subtitle: 'Use your fingerprint or face',
    negativeButtonText: 'Cancel',
  });

  const disableBio = async () => {
    try { await NativeBiometric.deleteCredentials({ server: BIO_SERVER }); } catch (e) { /* nothing stored */ }
    try { localStorage.removeItem(BIO_ON_KEY); } catch (e) { /* storage blocked */ }
    setBioOn(false);
  };

  const handleBio = async () => {
    setError('');
    try { await bioPrompt(); } catch (e) { return; }
    let cred = null;
    try { cred = await NativeBiometric.getCredentials({ server: BIO_SERVER }); } catch (e) { cred = null; }
    if (!cred || !cred.username || !cred.password) {
      await disableBio();
      setError('Fingerprint sign-in is not available. Please sign in with your password.');
      return;
    }
    const res = await signInWith(cred.username, cred.password, false);
    if (res.invalid) {
      await disableBio();
      setError('Your saved sign-in is out of date. Please sign in with your password.');
    }
  };

  const offerTurnOn = async () => {
    setBioOffer(false);
    let ok = false;
    try { await bioPrompt(); ok = true; } catch (e) { ok = false; }
    await signInWith(userId, password, ok);
  };

  const offerNotNow = async () => {
    try { localStorage.setItem(BIO_DECLINED_KEY, '1'); } catch (e) { /* storage blocked */ }
    setBioOffer(false);
    await signInWith(userId, password, false);
  };

  // ── Vault geometry ──────────────────────────────────────────────────────────
  const { w: W, h: H } = vaultSize;
  const cx = W / 2, cy = H / 2;
  const rx = Math.max(90, W / 2 - 62);
  const ry = Math.max(80, H / 2 - 46);

  const nodes = FEATURES.map((f, i) => {
    const ang = (-Math.PI / 2) + (i * 2 * Math.PI / FEATURES.length);
    const px = cx + Math.cos(ang) * rx;
    const py = cy + Math.sin(ang) * ry;
    const slot = REVEAL_ORDER.indexOf(i);
    return {
      ...f,
      x: px, y: py,
      leftPx: px.toFixed(1) + 'px',
      topPx:  py.toFixed(1) + 'px',
      opacity: vaultStage2 ? 1 : 0,
      scale:   vaultStage2 ? 1 : 0.4,
      delay:  (0.34 + slot * 0.11).toFixed(2) + 's',
    };
  });

  const links = nodes.map((n, i) => {
    const dx = n.x - cx, dy = n.y - cy;
    const dist = Math.hypot(dx, dy);
    const ux = dx / dist, uy = dy / dist;
    const startGap = boxHit(ux, uy, 23); // 46px hub half-extent
    const endGap   = boxHit(ux, uy, 21); // 42px module tile half-extent
    const slot = REVEAL_ORDER.indexOf(i);
    return {
      x1: (cx + ux * startGap).toFixed(1),            y1: (cy + uy * startGap).toFixed(1),
      x2: (cx + ux * (dist - endGap)).toFixed(1),     y2: (cy + uy * (dist - endGap)).toFixed(1),
      opacity: vaultStage1 ? 1 : 0,
      delay:  (0.18 + slot * 0.11).toFixed(2) + 's',
    };
  });


  const isIOS = platform === 'ios';

  const forgotBtnStyle = { background: 'none', border: 'none', padding: '8px 6px', margin: '-8px -6px', font: 'inherit', color: 'inherit', cursor: 'pointer' };

  const devFooter = (
    <div style={{ textAlign: 'center', fontSize: 11, letterSpacing: '0.05em', color: '#A9AEB8' }}>
      Developed by <span style={{ fontWeight: 500, color: '#8E94A0', letterSpacing: '0.06em' }}>Kodrel</span>
    </div>
  );

  const mobileIdContainerStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#F4F3F8',
    padding: '0 14px',
    marginBottom: 18,
    border: `1.5px solid ${idFocused ? ACCENT : IDLE_BORDER}`,
    borderRadius: 12,
    transition: 'border-color .18s ease',
  };

  const mobilePwContainerStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#F4F3F8',
    padding: '0 14px',
    marginBottom: 22,
    border: `1.5px solid ${pwFocused ? ACCENT : IDLE_BORDER}`,
    borderRadius: 12,
    transition: 'border-color .18s ease',
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .lg-root { font-family: 'Hanken Grotesk', system-ui, sans-serif; -webkit-font-smoothing: antialiased; }
        .lg-root, .lg-root *, .lg-root *::before, .lg-root *::after { -webkit-tap-highlight-color: transparent; }
        @keyframes floatBlob  { 0%,100% { transform: translateY(0) translateX(0); }  50% { transform: translateY(18px) translateX(-14px); } }
        @keyframes floatBlob2 { 0%,100% { transform: translateY(0) translateX(0); }  50% { transform: translateY(-16px) translateX(12px); } }
        @keyframes fadeUp     { from { opacity: 0; transform: translateY(12px); }    to  { opacity: 1; transform: translateY(0); } }
        @keyframes overlayIn  { from { opacity: 0; }                                  to  { opacity: 1; } }
        @keyframes swirlOrbit { to { transform: rotate(360deg); } }
        @keyframes dotPulse   { 0%,80%,100% { opacity: .25; transform: scale(.8); } 40% { opacity: 1; transform: scale(1); } }
        .lg-btn:hover { filter: brightness(1.07); }
        .lg-tile { transition: transform .25s cubic-bezier(.2,.8,.2,1), background .25s ease, border-color .25s ease, box-shadow .25s ease; }
        .lg-node:hover .lg-tile  { transform: scale(1.22); background: oklch(0.52 0.16 285 / 0.55) !important; border-color: rgba(255,255,255,.5) !important; box-shadow: 0 0 26px oklch(0.6 0.17 290 / 0.65); }
        .lg-node:hover .lg-label { color: #fff !important; }
        input::placeholder { color: #A9AEB8; }
        .lg-left input:-webkit-autofill,
        .lg-left input:-webkit-autofill:hover,
        .lg-left input:-webkit-autofill:focus {
          -webkit-text-fill-color: #1E2128;
          -webkit-box-shadow: 0 0 0 1000px oklch(0.95 0.025 285) inset;
          box-shadow: 0 0 0 1000px oklch(0.95 0.025 285) inset;
          transition: background-color 9999s ease-in-out 0s;
          caret-color: #1E2128;
        }
        .lg-mobile input:-webkit-autofill,
        .lg-mobile input:-webkit-autofill:hover,
        .lg-mobile input:-webkit-autofill:focus {
          -webkit-text-fill-color: #1E2128;
          -webkit-box-shadow: 0 0 0 1000px #F4F3F8 inset;
          box-shadow: 0 0 0 1000px #F4F3F8 inset;
          transition: background-color 9999s ease-in-out 0s;
          caret-color: #1E2128;
        }
        .lg-grid { display: grid; grid-template-columns: 1fr 1.1fr; }
        .lg-right-panel { perspective: 2400px; }
        .lg-panel-3d { transform: rotateY(-15deg) scale(0.95); box-shadow: 0 30px 50px -26px rgba(20, 10, 60, 0.6); animation: panelFloat 6s ease-in-out infinite; }
        @keyframes panelFloat { 0%, 100% { transform: rotateY(-15deg) scale(0.95) translateY(0); } 50% { transform: rotateY(-15deg) scale(0.95) translateY(-7px); } }
        @media (prefers-reduced-motion: reduce) { .lg-panel-3d { animation: none; } }
        .lg-m-hero { position: relative; z-index: 2; height: clamp(190px, 28vh, 250px); flex-shrink: 0; overflow: hidden; display: flex; flex-direction: column; align-items: center; justify-content: center;
          background: linear-gradient(160deg, oklch(0.4 0.12 288) 0%, oklch(0.31 0.1 285) 55%, oklch(0.25 0.08 295) 100%);
          border-radius: 0 0 ${HERO_RADIUS}px ${HERO_RADIUS}px; box-shadow: ${HERO_SHADOW_CSS};
          transition: transform .75s cubic-bezier(.7, 0, .2, 1); will-change: transform; }
        .lg-m-hero.lg-open { transform: translateY(-120%); }
        .lg-m-form { position: relative; z-index: 1; flex: 1; min-height: 0; overflow-y: auto; padding: 28px 22px 0; background: #fff; scrollbar-width: none;
          transition: transform .75s cubic-bezier(.7, 0, .2, 1); will-change: transform; }
        .lg-m-form::-webkit-scrollbar { display: none; }
        .lg-m-form.lg-open { transform: translateY(105%); }
        .lg-m-under { position: absolute; inset: 0; z-index: 0; background: #fff; display: flex; align-items: center; justify-content: center; }
        .lg-mobile input { -webkit-appearance: none; appearance: none; background: transparent !important; box-shadow: none; border-radius: 0; outline: none; }
        .lg-mobile input:focus { background: transparent !important; outline: none; }
        .lg-m-e { transform-origin: 50% 100%; animation: lgEDrop 1.1s cubic-bezier(.3, 0, .4, 1) .15s both; }
        .lg-m-word { animation: lgWordDip 1.1s ease .15s both; }
        .lg-m-tag { margin-top: 9px; font-size: 12.5px; font-weight: 500; letter-spacing: .09em; color: rgba(255, 255, 255, .74); animation: lgTagIn .55s ease 1.2s both; }
        @keyframes lgEDrop {
          0%   { transform: translateY(-90px); opacity: 0; }
          10%  { opacity: 1; }
          44%  { transform: translateY(0) scaleY(1); }
          50%  { transform: translateY(0) scaleY(.86); }
          68%  { transform: translateY(-17px) scaleY(1.04); }
          84%  { transform: translateY(0) scaleY(.96); }
          100% { transform: translateY(0) scaleY(1); opacity: 1; }
        }
        @keyframes lgWordDip { 0%, 42% { transform: translateY(0); } 50% { transform: translateY(3px); } 66%, 100% { transform: translateY(0); } }
        @keyframes lgTagIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .lg-m-hero, .lg-m-form { transition: none; }
          .lg-m-e, .lg-m-word, .lg-m-tag { animation: none; }
        }
        .lg-mobile { display: none; }
        @media (max-width: 640px) {
          .lg-grid { display: block; }
          .lg-left { display: none !important; }
          .lg-right-panel { display: none !important; }
          .lg-overlay-desk { display: none !important; }
          .lg-mobile { display: flex; flex-direction: column; position: absolute; inset: 0; background: #fff; }
        }
      `}</style>

      <div className="lg-root lg-grid" style={{ position: 'relative', height: '100vh', overflow: 'hidden', background: 'oklch(0.95 0.025 285)' }}>

        {/* ── LEFT — credential form ───────────────────────────────────────── */}
        <div className="lg-left" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 40px', minHeight: 0, overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 360, animation: 'fadeUp .55s cubic-bezier(.2,.8,.2,1) both' }}>

            {/* Brand lockup */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 22 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                <EdEaseMark size={29} />
              </div>
              <EdEaseWordmark height={18} />
            </div>

            <p style={{ margin: '0 0 6px', fontSize: 13.5, color: '#6B7280', lineHeight: 1.5 }}>
              Please enter your credentials to access your school dashboard.
            </p>
            <div style={{ fontSize: 19, fontWeight: 700, color: ACCENT_MED, letterSpacing: '-0.01em', marginBottom: 30 }}>
              Welcome <span style={{ color: '#3A3F4A', fontWeight: 500 }}>back</span>
            </div>

            <form onSubmit={handleSubmit}>
              {/* User ID */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1.5px solid ${userId ? ACCENT : IDLE_BORDER}`, padding: '0 2px 2px', marginBottom: 26, transition: 'border-color .18s ease' }}>
                <User size={19} color={idFocused ? ACCENT : '#9AA0AC'} style={{ flexShrink: 0, transition: 'color .18s ease' }} />
                <input
                  value={userId}
                  onChange={e => setUserId(e.target.value)}
                  onFocus={() => setIdFocused(true)}
                  onBlur={() => setIdFocused(false)}
                  placeholder="Your user ID"
                  autoComplete="username"
                  style={{ fontFamily: 'inherit', flex: 1, fontSize: 14.5, color: '#1E2128', border: 'none', padding: '11px 0 15px', outline: 'none', background: 'transparent', minWidth: 0 }}
                />
              </div>

              {/* Password */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1.5px solid ${password ? ACCENT : IDLE_BORDER}`, padding: '0 2px 2px', marginBottom: 24, transition: 'border-color .18s ease' }}>
                <Lock size={19} color={pwFocused ? ACCENT : '#9AA0AC'} style={{ flexShrink: 0, transition: 'color .18s ease' }} />
                <input
                  type={pwVisible ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setPwFocused(true)}
                  onBlur={() => setPwFocused(false)}
                  placeholder="Password"
                  autoComplete="current-password"
                  style={{ fontFamily: 'inherit', flex: 1, fontSize: 14.5, color: '#1E2128', border: 'none', padding: '11px 0', outline: 'none', background: 'transparent', minWidth: 0 }}
                />
                <button
                  type="button"
                  onClick={() => setPwVisible(v => !v)}
                  style={{ color: '#9AA0AC', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexShrink: 0, padding: 0 }}
                >
                  {pwVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Remember me */}
              <div onClick={() => setRemember(r => !r)} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: (error || loginNotice) ? 16 : 30, cursor: 'pointer' }}>
                <div style={{ width: 38, height: 21, borderRadius: 999, background: remember ? ACCENT : '#fff', border: `1.5px solid ${remember ? ACCENT : '#C9CCD3'}`, padding: 2, display: 'flex', justifyContent: remember ? 'flex-end' : 'flex-start', transition: 'all .2s ease', flexShrink: 0 }}>
                  <div style={{ width: 15, height: 15, borderRadius: 999, background: remember ? '#fff' : '#C9CCD3', transition: 'all .2s ease' }} />
                </div>
                <span style={{ fontSize: 13.5, color: '#5A606C' }}>Remember me</span>
              </div>

              {/* Inline error */}
              {(error || loginNotice) && (
                <div style={{ marginBottom: 16, padding: '9px 12px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, fontSize: 13, color: '#dc2626', lineHeight: 1.4 }}>
                  {error || loginNotice}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="lg-btn"
                disabled={submitting}
                style={{ width: '100%', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: '#fff', background: ACCENT, border: 'none', padding: '14px 0', borderRadius: 12, cursor: submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, boxShadow: '0 12px 24px -12px oklch(0.52 0.16 285 / 0.6)', transition: 'filter .15s ease', opacity: submitting ? 0.7 : 1 }}
              >
                <LockKeyhole size={16} />Login
              </button>
            </form>

            <p style={{ textAlign: 'center', margin: '26px 0 0', fontSize: 13.5, color: '#6B7280' }}>
              <button type="button" onClick={() => setForgotOpen(true)} style={forgotBtnStyle}><span style={{ color: ACCENT_MED, fontWeight: 700 }}>Forgot</span>{' '}
              your <span style={{ fontWeight: 700, color: '#3A3F4A' }}>password?</span></button>
            </p>
            <div style={{ marginTop: 28 }}>{devFooter}</div>
          </div>
        </div>

        {/* ── RIGHT — brand panel ──────────────────────────────────────────── */}
        <div className="lg-right-panel" style={{ position: 'relative', padding: '18px 18px 18px 0', minHeight: 0 }}>
          <div className="lg-panel-3d" style={{ position: 'relative', overflow: 'hidden', height: '100%', borderRadius: 26, background: 'linear-gradient(160deg, oklch(0.4 0.12 288) 0%, oklch(0.31 0.1 285) 55%, oklch(0.25 0.08 295) 100%)', display: 'flex', flexDirection: 'column', padding: 40 }}>

            {/* Decorative blobs */}
            <div style={{ position: 'absolute', width: 480, height: 480, borderRadius: 999, pointerEvents: 'none', background: 'radial-gradient(circle, oklch(0.62 0.16 285 / 0.35), transparent 70%)', top: -170, right: -120, animation: 'floatBlob 13s ease-in-out infinite' }} />
            <div style={{ position: 'absolute', width: 380, height: 380, borderRadius: 999, pointerEvents: 'none', background: 'radial-gradient(circle, oklch(0.72 0.12 305 / 0.22), transparent 70%)', bottom: -140, left: -100, animation: 'floatBlob2 15s ease-in-out infinite' }} />
            {/* Dot grid */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.07, backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

            {/* Heading */}
            <div style={{ position: 'relative', textAlign: 'center', marginBottom: 26, pointerEvents: 'none' }}>
              <h1 style={{ margin: '0 0 10px', fontSize: 38, lineHeight: 1.1, fontWeight: 700, letterSpacing: '-0.025em', color: '#fff' }}>Welcome Back!</h1>
              <p style={{ margin: '0 auto', fontSize: 17, lineHeight: 1.5, color: 'rgba(255,255,255,0.62)', maxWidth: 400 }}>Your vision in one place</p>
            </div>

            {/* Vault container */}
            <div ref={vaultRef} style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>

              {/* SVG: connector lines */}
              <svg
                viewBox={`0 0 ${W.toFixed(1)} ${H.toFixed(1)}`}
                preserveAspectRatio="none"
                fill="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
              >
                {links.map((ln, i) => (
                  <line
                    key={i}
                    x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2}
                    stroke="rgba(255,255,255,0.22)" strokeWidth="1"
                    opacity={ln.opacity}
                    style={{ transition: `opacity .4s ease ${ln.delay}` }}
                  />
                ))}
              </svg>

              {/* Hub tile */}
              <div style={{ position: 'absolute', width: 46, height: 46, borderRadius: 13, background: ACCENT_MED, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', opacity: vaultStage1 ? 1 : 0, transform: `scale(${vaultStage1 ? 1 : 0.5})`, transition: 'opacity .6s cubic-bezier(.2,.8,.2,1), transform .6s cubic-bezier(.2,.8,.2,1)', boxShadow: '0 0 30px oklch(0.52 0.16 285 / 0.6)', zIndex: 2, pointerEvents: 'none' }}>
                <EdEaseMark size={31} />
              </div>

              {/* Module tiles */}
              {nodes.map((n, i) => (
                <div
                  key={i}
                  className="lg-node"
                  style={{ position: 'absolute', left: n.leftPx, top: n.topPx, width: 42, height: 42, transform: `translate(-50%, -50%) scale(${n.scale})`, opacity: n.opacity, transition: `opacity .5s ease ${n.delay}, transform .55s cubic-bezier(.2,.8,.2,1) ${n.delay}`, zIndex: 1, pointerEvents: 'auto' }}
                >
                  <div
                    className="lg-tile"
                    style={{ width: 42, height: 42, borderRadius: 12, border: '1.5px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                  >
                    <n.Icon size={19} />
                  </div>
                  <span
                    className="lg-label"
                    style={{ position: 'absolute', top: 48, left: '50%', transform: 'translateX(-50%)', width: 96, fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.72)', textAlign: 'center', lineHeight: 1.25, transition: 'color .25s ease', pointerEvents: 'none', whiteSpace: 'normal' }}
                  >
                    {n.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── MOBILE LAYOUT — hidden on desktop via .lg-mobile { display: none } ── */}
        <div className="lg-mobile">
          {/* Shutter underlay: revealed when the header slides up and the form slides down */}
          <div className="lg-m-under" aria-hidden={!submitting}>
            {submitting && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'overlayIn .3s ease .35s both' }}>
                <EdEaseLoader size={64} variant="ink" />
                <div style={{ fontSize: 15, fontWeight: 600, color: '#1E2128', letterSpacing: '-0.01em', margin: '24px 0 8px' }}>Signing you in</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0, 0.15, 0.3].map((delay, i) => (
                    <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: ACCENT, display: 'inline-block', animation: 'dotPulse 1.2s ease-in-out ' + delay + 's infinite' }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Header */}
          <div className={'lg-m-hero' + (submitting ? ' lg-open' : '')}>
            <div style={{ position: 'absolute', width: 330, height: 330, borderRadius: 999, pointerEvents: 'none', background: 'radial-gradient(circle, oklch(0.62 0.16 285 / 0.35), transparent 70%)', top: -120, right: -90, animation: 'floatBlob 13s ease-in-out infinite' }} />
            <div style={{ position: 'absolute', width: 270, height: 270, borderRadius: 999, pointerEvents: 'none', background: 'radial-gradient(circle, oklch(0.72 0.12 305 / 0.22), transparent 70%)', bottom: -110, left: -80, animation: 'floatBlob2 15s ease-in-out infinite' }} />
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.07, backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="lg-m-e" style={{ lineHeight: 0 }}><EdEaseMark size={46} /></div>
              <div className="lg-m-word" style={{ lineHeight: 0, marginTop: 10 }}><EdEaseWordmark height={26} color="#fff" /></div>
              <div className="lg-m-tag">Education made Easy</div>
            </div>
          </div>

          {/* Form */}
          <div className={'lg-m-form' + (submitting ? ' lg-open' : '')}>
            <form onSubmit={handleSubmit}>
              {/* User ID — iOS: fully bordered rounded, Android: flat-bottom underline */}
              <div style={mobileIdContainerStyle}>
                <User size={19} color={idFocused ? ACCENT : '#9AA0AC'} style={{ flexShrink: 0, transition: 'color .18s ease' }} />
                <input value={userId} onChange={e => setUserId(e.target.value)} onFocus={() => setIdFocused(true)} onBlur={() => setIdFocused(false)} placeholder="Enter User ID" autoComplete="username" style={{ fontFamily: 'inherit', flex: 1, fontSize: 15, color: '#1E2128', border: 'none', padding: '16px 0', outline: 'none', background: 'transparent', minWidth: 0 }} />
              </div>
              {/* Password */}
              <div style={mobilePwContainerStyle}>
                <Lock size={19} color={pwFocused ? ACCENT : '#9AA0AC'} style={{ flexShrink: 0, transition: 'color .18s ease' }} />
                <input type={pwVisible ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)} placeholder="Password" autoComplete="current-password" style={{ fontFamily: 'inherit', flex: 1, fontSize: 15, color: '#1E2128', border: 'none', padding: '16px 0', outline: 'none', background: 'transparent', minWidth: 0 }} />
                <button type="button" onClick={() => setPwVisible(v => !v)} style={{ color: '#9AA0AC', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexShrink: 0, padding: 0 }}>
                  {pwVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Remember me — iOS: pill toggle, Android: checkbox */}
              <div onClick={() => setRemember(r => !r)} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 26, cursor: 'pointer' }}>
                {isIOS ? (
                  <div style={{ width: 38, height: 21, borderRadius: 999, background: remember ? ACCENT : '#fff', border: `1.5px solid ${remember ? ACCENT : '#C9CCD3'}`, padding: 2, display: 'flex', justifyContent: remember ? 'flex-end' : 'flex-start', transition: 'all .2s ease', flexShrink: 0 }}>
                    <div style={{ width: 15, height: 15, borderRadius: 999, background: remember ? '#fff' : '#C9CCD3', transition: 'all .2s ease' }} />
                  </div>
                ) : (
                  <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${remember ? ACCENT : '#C9CCD3'}`, background: remember ? ACCENT : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .18s ease' }}>
                    {remember && <svg viewBox="0 0 12 12" width="12" height="12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                )}
                <span style={{ fontSize: 14, color: '#5A606C' }}>Remember me</span>
              </div>
              {(error || loginNotice) && <div style={{ marginBottom: 16, padding: '9px 12px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, fontSize: 13, color: '#dc2626', lineHeight: 1.4 }}>{error || loginNotice}</div>}
              <button type="submit" disabled={submitting} style={{ width: '100%', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: '#fff', background: ACCENT, border: 'none', padding: '16px 0', borderRadius: 999, cursor: submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, boxShadow: '0 10px 20px -10px oklch(0.52 0.16 285 / 0.6)', opacity: submitting ? 0.7 : 1 }}>
                <LockKeyhole size={17} />Login
              </button>
            </form>
            <p style={{ textAlign: 'center', margin: '22px 0 0', fontSize: 14, color: '#6B7280' }}>
              <button type="button" onClick={() => setForgotOpen(true)} style={forgotBtnStyle}><span style={{ color: ACCENT_MED, fontWeight: 700 }}>Forgot</span>{' '}
              your <span style={{ fontWeight: 700, color: '#3A3F4A' }}>password?</span></button>
            </p>
            {/* Biometric — iOS: Face ID, Android: Fingerprint */}
            <div role="button" onClick={bioOn ? handleBio : undefined} style={{ display: bioOn ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', gap: 9, margin: '24px 0 20px', color: ACCENT_MED, cursor: 'pointer' }}>
              {isIOS ? <ScanFace size={20} /> : <Fingerprint size={20} />}
              <span style={{ fontSize: 14, fontWeight: 600 }}>{isIOS ? 'Sign in with Face ID' : 'Sign in with fingerprint or face'}</span>
            </div>
            <div style={{ margin: '4px 0 12px' }}>{devFooter}</div>
            <div style={{ width: 108, height: 4, borderRadius: 999, background: '#1E2128', opacity: 0.3, margin: '0 auto 16px' }} />
          </div>
        </div>

        {forgotOpen && (
          <div onClick={() => setForgotOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(20, 10, 60, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, animation: 'overlayIn .2s ease both' }}>
            <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 340, background: '#fff', borderRadius: 16, padding: '22px 22px 18px', boxShadow: '0 24px 48px -20px rgba(20, 10, 60, 0.5)', animation: 'fadeUp .25s ease both' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1E2128', marginBottom: 8 }}>Reset your password</div>
              <div style={{ fontSize: 13.5, color: '#5A606C', lineHeight: 1.55 }}>{userId.trim() ? 'Ask your school admin to reset it for you. Share your User ID:' : 'Ask your school admin to reset it for you. Tell them your User ID.'}</div>
              {userId.trim() && (
                <div style={{ margin: '10px 0 2px' }}><span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12.5, background: '#F4F3F8', border: '1px solid #E7E8EC', borderRadius: 8, padding: '3px 9px', color: '#1E2128' }}>{userId.trim()}</span></div>
              )}
              <div style={{ fontSize: 13.5, color: '#5A606C', lineHeight: 1.55, marginTop: 10 }}>You will get a temporary password and choose a new one when you sign in.</div>
              <button type="button" onClick={() => setForgotOpen(false)} style={{ width: '100%', marginTop: 16, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: '#fff', background: ACCENT, border: 'none', padding: '12px 0', borderRadius: 999, cursor: 'pointer' }}>Got it</button>
            </div>
          </div>
        )}

        {bioOffer && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'rgba(20, 10, 60, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, animation: 'overlayIn .2s ease both' }}>
            <div role="dialog" aria-modal="true" style={{ width: '100%', maxWidth: 340, background: '#fff', borderRadius: 16, padding: '22px 22px 18px', boxShadow: '0 24px 48px -20px rgba(20, 10, 60, 0.5)', animation: 'fadeUp .25s ease both' }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: '#F5F3FA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, color: ACCENT_MED }}><Fingerprint size={21} /></div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1E2128', marginBottom: 6 }}>Use fingerprint next time?</div>
              <div style={{ fontSize: 13.5, color: '#5A606C', lineHeight: 1.55 }}>Sign in with your fingerprint or face unlock instead of typing your password.</div>
              <button type="button" onClick={offerTurnOn} style={{ width: '100%', marginTop: 16, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: '#fff', background: ACCENT, border: 'none', padding: '12px 0', borderRadius: 999, cursor: 'pointer' }}>Turn on</button>
              <button type="button" onClick={offerNotNow} style={{ width: '100%', marginTop: 6, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: ACCENT_MED, background: 'none', border: 'none', padding: '10px 0', cursor: 'pointer' }}>Not now</button>
            </div>
          </div>
        )}
        {/* ── LOADING OVERLAY ──────────────────────────────────────────────── */}
        {submitting && (
          <div className="lg-overlay-desk" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg, oklch(0.2 0.09 290) 0%, oklch(0.15 0.07 285) 100%)', animation: 'overlayIn .25s ease both' }}>
            <div style={{ position: 'relative', width: 140, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: 999, border: '2px dashed rgba(255,255,255,0.18)', animation: 'swirlOrbit 5s linear infinite' }} />
              <div style={{ position: 'absolute', inset: 16, borderRadius: 999, background: 'radial-gradient(circle, oklch(0.52 0.16 285 / 0.35), transparent 70%)' }} />
              <EdEaseLoader size={64} style={{ position: 'relative' }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em', marginBottom: 8 }}>Signing you in</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[0, 0.15, 0.3].map((delay, i) => (
                <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', display: 'inline-block', animation: `dotPulse 1.2s ease-in-out ${delay}s infinite` }} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
